<template>
    <div class="na-form">
        <div class="staBtTitle">
            <div class="staTitle col">Data Import</div>
        </div>

        <DxTabPanel
            :selected-index="currentStepIndex"
            :swipe-enabled="false"
            @update:selected-index="goToStep($event)">
            <DxItem :title="stepTitle(0, '1. Select Types')">
                <template #default>
                    <div class="import-step1">
                        <div style="margin-bottom: 16px; padding: 12px; background: #f0f4f8; border-radius: 4px; color: #333; font-size: 0.9em; line-height: 1.5;">
                            Select the topic types you want to import data for. Optionally, select link types to create connections between topics.
                            After selecting, click <strong>Download Template</strong> to get a pre-formatted Excel file with the correct columns for your selection.
                            Fill in the template and proceed to the next step.
                            If you already have a filled-in template from a previous download, you can skip this step and go directly to <strong>2. Upload File</strong>.
                        </div>
                        <div style="margin-bottom: 12px; font-weight: 600;">Select Topic Types to Import</div>

                        <DxTagBox
                            :data-source="importableTypes"
                            :show-selection-controls="true"
                            :show-clear-button="true"
                            display-expr="TtyName"
                            value-expr="TtyId"
                            :search-enabled="true"
                            placeholder="Select one or more topic types..."
                            v-model:value="selectedTtyIds"
                        />
                        <span v-if="importableTypesError" style="color: #b00020; display: block; margin-top: 4px; font-size: 0.85em;">
                            Could not load topic types: {{ importableTypesError }}
                        </span>

                        <div style="margin-top: 16px; margin-bottom: 8px; font-weight: 600;">Link Types (optional)</div>

                        <DxTagBox
                            :data-source="availableLinkTypes"
                            :show-selection-controls="true"
                            :show-clear-button="true"
                            display-expr="name"
                            value-expr="id"
                            :search-enabled="true"
                            placeholder="Select link types to include..."
                            v-model:value="selectedTltIds"
                        />
                        <span v-if="linkTypesError" style="color: #856404; display: block; margin-top: 4px; font-size: 0.85em;">
                            Could not load link types: {{ linkTypesError }}
                        </span>

                        <div style="margin-top: 12px;">
                            <DxButton
                                text="Download Template"
                                type="success"
                                :disabled="isDownloading || selectedTtyIds.length === 0"
                                @click="downloadTemplate"
                            />
                            <span v-if="downloadError" style="color: red; display: block; margin-top: 4px; font-size: 0.85em;">
                                {{ downloadError }}
                            </span>
                        </div>

                        <div style="margin-top: 16px;">
                            <DxButton
                                text="Next"
                                type="default"
                                :disabled="selectedTtyIds.length === 0"
                                @click="goToStep(1)"
                            />
                        </div>
                    </div>
                </template>
            </DxItem>
            <DxItem :title="stepTitle(1, '2. Upload File')">
                <template #default>
                    <div class="import-step2" style="max-width: 600px; padding: 16px 0;">
                        <div style="margin-bottom: 16px; padding: 12px; background: #f0f4f8; border-radius: 4px; color: #333; font-size: 0.9em; line-height: 1.5;">
                            Upload the filled-in Excel template you downloaded in the previous step.
                            The system will validate the file structure and check each row for errors.
                        </div>
                        <div style="margin-bottom: 12px; font-weight: 600;">Upload your completed import template (.xlsx)</div>

                        <DxFileUploader
                            :multiple="false"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            upload-mode="useButtons"
                            @value-changed="onFileSelected"
                            :show-file-list="true"
                            select-button-text="Choose File"
                        />

                        <div style="margin-top: 16px; display: flex; gap: 8px; align-items: center;">
                            <DxButton
                                text="Upload &amp; Parse"
                                type="default"
                                :disabled="!selectedFile || isParsing"
                                @click="uploadAndParse"
                            />
                            <DxButton
                                text="Back"
                                type="normal"
                                @click="goToStep(0)"
                            />
                        </div>

                        <div v-if="isParsing" style="margin-top: 16px; display: flex; align-items: center; gap: 8px; color: #555;">
                            <span class="import-spinner"></span>
                            <span>Uploading and parsing file, please wait...</span>
                        </div>

                        <div v-if="parseError" style="margin-top: 12px; color: #721c24; background: #f8d7da; border: 1px solid #f5c6cb; border-radius: 4px; padding: 8px 12px;">
                            {{ parseError }}
                        </div>
                    </div>
                </template>
            </DxItem>
            <DxItem :title="stepTitle(2, '3. Validate &amp; Review')">
                <template #default>
                    <div class="import-step3" style="padding: 8px 0;">
                        <div style="margin-bottom: 12px; padding: 12px; background: #f0f4f8; border-radius: 4px; color: #333; font-size: 0.9em; line-height: 1.5;">
                            Review the parsed data below. Rows with errors are highlighted in red — you can fix values by clicking on a cell to edit it inline.
                            Rows marked as <strong>Exists in DB</strong> were found because their title matches an existing topic of the same type (case-insensitive). You can choose to <strong>Skip</strong> them or <strong>Update</strong> the existing topic with the imported values.
                            Rows with the same Import Key within the file are flagged as file duplicates — select which one to keep.
                            Once all errors are resolved, click <strong>Import</strong> to proceed.
                        </div>
                        <!-- Summary bar -->
                        <div class="import-summary-bar">
                            <span class="import-badge import-badge-new">{{ importSummary.new }} New</span>
                            <span class="import-badge import-badge-update">{{ importSummary.updates }} Updates</span>
                            <span class="import-badge import-badge-skip">{{ importSummary.skipped }} Skipped</span>
                            <span class="import-badge import-badge-error">{{ importSummary.errors }} Errors</span>
                        </div>

                        <!-- Bulk duplicate actions -->
                        <div v-if="totalDuplicateDbRows > 0" style="margin-bottom: 8px;">
                            <span style="font-weight: 600; margin-right: 8px;">Duplicates ({{ totalDuplicateDbRows }}):</span>
                            <DxButton text="Skip All Duplicates" type="normal" @click="bulkDuplicateAction('Skip')" style="margin-right: 8px;" />
                            <DxButton text="Update All Duplicates" type="default" @click="bulkDuplicateAction('Update')" />
                        </div>

                        <!-- Toolbar: errors-only toggle + back button -->
                        <div style="margin: 8px 0;">
                            <DxButton
                                :text="showErrorsOnly ? 'Show All Rows' : 'Show Errors Only'"
                                :type="showErrorsOnly ? 'default' : 'danger'"
                                @click="showErrorsOnly = !showErrorsOnly"
                            />
                            <DxButton text="Back to Upload" type="normal" @click="goToStep(1)" style="margin-left: 8px;" />
                        </div>

                        <!-- Inner DxTabPanel: one tab per sheet + TopicLinks tab -->
                        <DxTabPanel :defer-rendering="false">
                            <DxItem v-for="tab in sheetTabs" :key="tab.ttyId" :title="tab.sheetName">
                                <template #default>
                                    <DxDataGrid
                                        :ref="'grid_' + tab.ttyId"
                                        :data-source="flatRows[tab.ttyId]"
                                        :show-borders="true"
                                        :column-auto-width="true"
                                        :filter-value="errorsOnlyFilter"
                                        @cell-prepared="onCellPrepared"
                                        @editor-preparing="onEditorPreparing"
                                        @row-updating="onRowUpdating"
                                        key-expr="_rowIndex"
                                    >
                                        <DxEditing :allow-updating="true" mode="cell" />
                                        <DxColumn data-field="_hasErrors" :visible="false" />
                                        <DxColumn data-field="_isDuplicateDb" :visible="false" />
                                        <DxColumn data-field="_isDuplicateFile" :visible="false" />
                                        <DxColumn data-field="_importKey" caption="Import Key" :allow-editing="false" :width="100" />
                                        <DxColumn
                                            data-field="_rowAction"
                                            caption="Action"
                                            :width="140"
                                            :visible="hasSheetDuplicates(tab.ttyId)"
                                            :allow-editing="false"
                                            cell-template="rowActionTemplate"
                                        />
                                        <DxColumn
                                            v-for="col in tab.columns"
                                            :key="col.fieldName"
                                            :data-field="col.fieldName"
                                            :caption="col.caption"
                                            :allow-editing="true"
                                        />
                                        <template #rowActionTemplate="{ data: cellData }">
                                            <span v-if="cellData.data._isDuplicateDb" style="display: flex; align-items: center; gap: 4px;">
                                                <span style="color: #856404; font-size: 0.8em; white-space: nowrap;">Exists in DB</span>
                                                <DxSelectBox
                                                    :value="cellData.data._rowAction"
                                                    :items="['Skip', 'Update']"
                                                    @value-changed="onRowActionChanged(cellData.data, tab.ttyId, $event)"
                                                    :width="90"
                                                />
                                            </span>
                                            <span v-else-if="cellData.data._isDuplicateFile">
                                                <DxButton
                                                    :text="cellData.data._fileGroupSelected ? 'Keep (selected)' : 'Keep this'"
                                                    :type="cellData.data._fileGroupSelected ? 'success' : 'normal'"
                                                    :style="{ fontSize: '0.8em' }"
                                                    @click="toggleFileDuplicate(cellData.data, tab.ttyId)"
                                                />
                                            </span>
                                            <span v-else style="color: #155724;">New</span>
                                        </template>
                                    </DxDataGrid>
                                </template>
                            </DxItem>
                            <DxItem
                                v-for="tltIdStr in Object.keys(linkColumnsByTltId || {})"
                                :key="'links-' + tltIdStr"
                                :title="getLinkTabTitle(tltIdStr)"
                            >
                                <template #default>
                                    <div v-if="linkTypeErrorCount(parseInt(tltIdStr)) > 0" class="link-error-bar">
                                        <span class="error-count">{{ linkTypeErrorCount(parseInt(tltIdStr)) }} error(s)</span>
                                        <DxCheckBox
                                            :text="'Show Errors Only'"
                                            :value="(linkErrorsOnlyByTltId || {})[tltIdStr] || false"
                                            @value-changed="e => toggleLinkErrorFilter(tltIdStr, e.value)"
                                        />
                                    </div>
                                    <DxDataGrid
                                        :ref="'linkGrid_' + tltIdStr"
                                        :data-source="filteredLinkRowsByTltId(parseInt(tltIdStr))"
                                        :show-borders="true"
                                        :column-auto-width="true"
                                        @cell-prepared="onLinkCellPrepared"
                                        @editor-preparing="onLinkEditorPreparing"
                                        @row-updating="e => onLinkRowUpdating(e, parseInt(tltIdStr))"
                                        key-expr="_rowIndex"
                                    >
                                        <DxEditing :allow-updating="true" mode="cell" />
                                        <DxColumn data-field="_hasErrors" :visible="false" />
                                        <DxColumn data-field="_sourceImportKey" caption="Source Import Key" />
                                        <DxColumn data-field="_targetImportKey" caption="Target Import Key" />
                                        <DxColumn
                                            v-for="col in ((linkColumnsByTltId || {})[tltIdStr] || {}).columns || []"
                                            :key="col.fieldName"
                                            :data-field="col.fieldName"
                                            :caption="col.caption"
                                            :allow-editing="true"
                                        />
                                    </DxDataGrid>
                                </template>
                            </DxItem>
                        </DxTabPanel>

                        <!-- Import commit gate -->
                        <div style="margin-top: 16px; display: flex; align-items: center; gap: 12px;">
                            <DxButton
                                text="Import"
                                type="success"
                                :disabled="hasAnyError"
                                @click="goToStep(3)"
                            />
                            <span v-if="hasAnyError" style="color: #721c24; font-size: 0.85em;">
                                Fix all validation errors before importing ({{ importSummary.errors }} remaining)
                            </span>
                        </div>
                    </div>
                </template>
            </DxItem>
            <DxItem :title="stepTitle(3, '4. Import')">
                <template #default>
                    <div class="import-step-container" style="padding: 16px;">
                        <div style="margin-bottom: 16px; padding: 12px; background: #f0f4f8; border-radius: 4px; color: #333; font-size: 0.9em; line-height: 1.5;">
                            Assign an initial status to each new topic before importing. You can apply a status to all topics of a type at once using the bulk selector,
                            or set it individually per row. Topics marked as <strong>Update</strong> or <strong>Skip</strong> do not require a status.
                        </div>
                        <!-- Final summary recap -->
                        <div style="margin-bottom: 16px; padding: 12px; background: #f8f9fa; border-radius: 4px;">
                            <h4 style="margin: 0 0 8px 0;">Import Summary</h4>
                            <div style="display: flex; gap: 24px;">
                                <span><strong>{{ importSummary.new }}</strong> new</span>
                                <span><strong>{{ importSummary.updates }}</strong> updates</span>
                                <span><strong>{{ importSummary.skipped }}</strong> skipped</span>
                                <span v-if="flatLinkRows.length > 0"><strong>{{ flatLinkRows.length }}</strong> link(s)</span>
                                <span v-if="importSummary.errors > 0" style="color: #dc3545;">
                                    <strong>{{ importSummary.errors }}</strong> errors (excluded)
                                </span>
                            </div>
                        </div>

                        <!-- Link-only imports: status accordion is empty because there are no topic rows -->
                        <div v-if="accordionItems.length === 0 && flatLinkRows.length > 0"
                             style="margin-bottom: 16px; padding: 12px; background: #e7f3ff; border-radius: 4px; color: #004085;">
                            This import contains only link rows — no topic statuses to pick. Click <strong>Import</strong> below to proceed.
                        </div>

                        <!-- Bulk apply helper: pick one status to apply across every applicable row -->
                        <div v-if="accordionItems.length > 0" style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 4px;">
                            <div style="font-weight: 600; margin-bottom: 8px;">Set status for all rows</div>
                            <DxSelectBox
                                :data-source="bulkStatusOptions"
                                display-expr="name"
                                value-expr="name"
                                v-model:value="bulkStatusName"
                                :disabled="bulkStatusOptions.length === 0"
                                :placeholder="bulkStatusOptions.length === 0
                                    ? 'No statuses are shared across all selected topic types — set each row individually'
                                    : (bulkIsMixed ? '(mixed)' : 'Pick a status to apply to all rows')"
                                :width="360"
                                @value-changed="onBulkStatusChanged"
                            />
                            <div v-if="missingStatusRowCount > 0" style="margin-top: 8px; color: #dc3545; font-size: 0.85em;">
                                {{ missingStatusRowCount }} row(s) still need a status before import can proceed.
                            </div>
                        </div>

                        <!-- Per-type DxAccordion containing the per-row status editor -->
                        <DxAccordion
                            :data-source="accordionItems"
                            :collapsible="true"
                            :multiple="true"
                            :selected-items="accordionItems"
                            style="margin-bottom: 16px;"
                        >
                            <template #title="{ data }">
                                <span><strong>{{ data.ttyName }}</strong> ({{ data.rows.length }} row{{ data.rows.length === 1 ? '' : 's' }})</span>
                            </template>
                            <template #item="{ data }">
                                <DxDataGrid
                                    :data-source="data.rows"
                                    key-expr="_rowIndex"
                                    :show-borders="true"
                                    :column-auto-width="true"
                                >
                                    <DxColumn data-field="_importKey" caption="Import Key" :allow-editing="false" :width="160" />
                                    <DxColumn data-field="_rowAction" caption="Action" :allow-editing="false" :width="100" />
                                    <DxColumn caption="Status" :width="260" cell-template="statusCellTemplate" />
                                    <template #statusCellTemplate="{ data: cell }">
                                        <template v-if="cell.data._rowAction !== 'Update' && !(cell.data._isDuplicateFile && !cell.data._fileGroupSelected) && cell.data._rowAction !== 'Skip'">
                                            <DxSelectBox
                                                v-if="getStatusListForRow(cell.data).length > 0"
                                                :data-source="getStatusListForRow(cell.data)"
                                                display-expr="name"
                                                value-expr="id"
                                                v-model:value="cell.data._statusId"
                                                @value-changed="onRowStatusChanged"
                                            />
                                            <span v-else style="color: #c00; font-size: 0.85em;">(no statuses available for this topic type)</span>
                                        </template>
                                        <span v-else-if="cell.data._rowAction === 'Update'" style="color: #888; font-style: italic;">(unchanged on update)</span>
                                        <span v-else style="color: #888; font-style: italic;">(skipped)</span>
                                    </template>
                                </DxDataGrid>
                            </template>
                        </DxAccordion>

                        <!-- Confirm button -->
                        <div style="margin-top: 24px;">
                            <DxButton
                                text="Confirm Import"
                                type="default"
                                styling-mode="contained"
                                :disabled="confirmDisabled"
                                @click="confirmImport"
                            />
                            <span v-if="isImporting" style="margin-left: 12px; color: #666;">
                                Importing...
                            </span>
                            <span v-if="importError" style="margin-left: 12px; color: #dc3545;">
                                {{ importError }}
                            </span>
                        </div>
                    </div>
                </template>
            </DxItem>
            <DxItem :title="stepTitle(4, '5. Results')">
                <template #default>
                    <div class="import-step5" style="padding: 8px 0;">
                        <div style="margin-bottom: 12px; padding: 12px; background: #f0f4f8; border-radius: 4px; color: #333; font-size: 0.9em; line-height: 1.5;">
                            Import complete. Review the results below. You can export the results to Excel or start a new import.
                        </div>
                        <div v-if="!importResult" style="color: #666;">
                            No import result available.
                        </div>
                        <div v-else>
                            <!-- Batch tracking warning banner (09-02) -->
                            <div
                                v-if="importResult && importResult.batchTrackingWarning"
                                class="import-batch-tracking-warning"
                                style="
                                    background-color: #fff3cd;
                                    border: 1px solid #ffc107;
                                    color: #856404;
                                    padding: 10px 14px;
                                    margin-bottom: 12px;
                                    border-radius: 4px;
                                    display: flex;
                                    align-items: center;
                                    gap: 8px;
                                "
                            >
                                <i class="dx-icon-warning" style="font-size: 18px;"></i>
                                <span>{{ importResult.batchTrackingWarning }}</span>
                            </div>
                            <!-- Summary badge bar -->
                            <div class="import-summary-bar" style="align-items: center; flex-wrap: wrap; gap: 8px;">
                                <span class="import-badge import-badge-new">{{ resultSummary.imported }} Imported</span>
                                <span class="import-badge import-badge-update">{{ resultSummary.updated }} Updated</span>
                                <span class="import-badge import-badge-skip">{{ resultSummary.skipped }} Skipped</span>
                                <span class="import-badge import-badge-error">{{ resultSummary.errored }} Errors</span>
                                <span style="color: #666; font-size: 0.85em; margin-left: 8px;">
                                    Batch {{ importResult.batchId > 0 ? '#' + importResult.batchId : 'N/A' }}
                                    <span v-if="importedAt"> | {{ importedAt.toLocaleString() }}</span>
                                    <span v-if="importResult.createdBy"> | {{ importResult.createdBy }}</span>
                                </span>
                            </div>

                            <!-- Status filter -->
                            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
                                <span style="font-weight: 600;">Filter:</span>
                                <DxSelectBox
                                    :items="['imported', 'updated', 'skipped', 'error']"
                                    v-model:value="resultStatusFilter"
                                    :show-clear-button="true"
                                    placeholder="All statuses"
                                    :width="180"
                                />
                            </div>

                            <!-- Topics grid -->
                            <div style="margin-bottom: 16px;">
                                <h4 style="margin: 0 0 6px 0;">Topics ({{ resultTopicRows.length }})</h4>
                                <DxDataGrid
                                    ref="resultTopicsGrid"
                                    :data-source="resultTopicRows"
                                    :show-borders="true"
                                    :column-auto-width="true"
                                    :filter-value="resultTopicFilter"
                                    @cell-prepared="onResultCellPrepared"
                                    key-expr="importKey"
                                >
                                    <DxScrolling mode="virtual" />
                                    <DxPaging :enabled="false" />
                                    <DxColumn data-field="importKey" caption="Import Key" :width="120" />
                                    <DxColumn data-field="topicId" caption="Topic ID" :width="90" />
                                    <DxColumn data-field="ttyName" caption="Topic Type" :width="140" />
                                    <DxColumn data-field="status" caption="Status" :width="100" />
                                    <DxColumn data-field="error" caption="Error" />
                                </DxDataGrid>
                            </div>

                            <!-- Links grid (only if there are link results) -->
                            <div v-if="resultLinkRows.length > 0" style="margin-bottom: 16px;">
                                <h4 style="margin: 0 0 6px 0;">Links ({{ resultLinkRows.length }})</h4>
                                <DxDataGrid
                                    ref="resultLinksGrid"
                                    :data-source="resultLinkRows"
                                    :show-borders="true"
                                    :column-auto-width="true"
                                    :filter-value="resultLinkFilter"
                                    @cell-prepared="onResultCellPrepared"
                                    key-expr="importKey"
                                >
                                    <DxScrolling mode="virtual" />
                                    <DxPaging :enabled="false" />
                                    <DxColumn data-field="importKey" caption="Import Key" :width="120" />
                                    <DxColumn data-field="tliId" caption="Link ID" :width="90" />
                                    <DxColumn data-field="status" caption="Status" :width="100" />
                                    <DxColumn data-field="error" caption="Error" />
                                </DxDataGrid>
                            </div>

                            <!-- Action buttons -->
                            <div style="display: flex; gap: 8px; margin-top: 8px;">
                                <DxButton
                                    text="Export Results"
                                    type="default"
                                    @click="exportResults"
                                />
                                <DxButton
                                    text="Start New Import"
                                    type="success"
                                    @click="resetWizard"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </DxItem>
        </DxTabPanel>
    </div>
</template>

<script>
import axios from 'axios'
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel'
import { DxTagBox } from 'devextreme-vue/tag-box'
import { DxButton } from 'devextreme-vue/button'
import { DxFileUploader } from 'devextreme-vue/file-uploader'
import { DxDataGrid, DxColumn, DxEditing, DxScrolling, DxPaging } from 'devextreme-vue/data-grid'
import { DxSelectBox } from 'devextreme-vue/select-box'
import { DxAccordion } from 'devextreme-vue/accordion'
import { DxCheckBox } from 'devextreme-vue/check-box'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'

export default {
    name: 'DataImport',
    components: {
        DxTabPanel,
        DxItem,
        DxTagBox,
        DxButton,
        DxFileUploader,
        DxDataGrid,
        DxColumn,
        DxEditing,
        DxScrolling,
        DxPaging,
        DxSelectBox,
        DxAccordion,
        DxCheckBox
    },
    data() {
        return {
            importableTypes: [],
            importableTypesError: null,
            selectedTtyIds: [],
            isDownloading: false,
            downloadError: null,

            // Step 1: link type selector (optional)
            selectedTltIds: [],
            availableLinkTypes: [],
            isLoadingLinkTypes: false,
            linkTypesError: null,

            currentStepIndex: 0,
            highestCompletedStep: -1,

            // Step 2: upload + parse
            selectedFile: null,
            isParsing: false,
            parseError: null,
            fileId: null,
            parseResult: null,

            // Step 3: validation grid data
            flatRows: {},
            flatLinkRows: [],
            linkColumns: [],
            linkColumnsByTltId: {},
            sheetTabs: [],
            showErrorsOnly: false,
            // Per-type "show errors only" toggle — keyed by tltId string
            linkErrorsOnlyByTltId: {},

            // Step 4: import execution
            // Per-workflow statuses keyed by ttyId: { [ttyId]: [{ id, name, description, isInitial, staOrder }] }
            statusesByTtyId: {},
            // Bulk apply helper currently selected status NAME (Pitfall 5: intersect by name, apply per-type id)
            bulkStatusName: null,
            // Becomes true once any individual row diverges from the bulk pick
            bulkIsMixed: false,
            isImporting: false,
            importError: null,
            importResult: null,  // { success, batchId, results } — stored for Phase 8
            importedAt: null,           // Date() captured at import success
            resultStatusFilter: null    // null | "imported" | "updated" | "skipped" | "error"
        }
    },
    computed: {
        importSummary() {
            let newCount = 0
            let updates = 0
            let skipped = 0
            let errors = 0

            for (const ttyId of Object.keys(this.flatRows)) {
                const rows = this.flatRows[ttyId] || []
                for (const row of rows) {
                    const hasErrors = Object.keys(row._cellErrors || {}).length > 0
                    if (hasErrors) {
                        errors++
                    } else if (row._isDuplicateFile && !row._fileGroupSelected) {
                        // File duplicate rows not selected count as skipped
                        skipped++
                    } else if (row._rowAction === 'Skip') {
                        skipped++
                    } else if (row._rowAction === 'Update') {
                        updates++
                    } else {
                        newCount++
                    }
                }
            }

            for (const row of (this.flatLinkRows || [])) {
                const hasErrors = Object.keys(row._cellErrors || {}).length > 0 || !!row._linkError
                if (hasErrors) errors++
                // Note: link rows do NOT contribute to new/update/skip buckets — those are topic-row concepts
            }

            return { new: newCount, updates, skipped, errors }
        },

        hasAnyError() {
            return this.importSummary.errors > 0
        },

        errorsOnlyFilter() {
            if (this.showErrorsOnly) {
                return [['_hasErrors', '=', true], 'or', ['_isDuplicateDb', '=', true], 'or', ['_isDuplicateFile', '=', true]]
            }
            return null
        },

        // Separate filter for the link grid — link rows do NOT have _isDuplicateDb / _isDuplicateFile,
        // so referencing those fields in errorsOnlyFilter throws DevExtreme E1047.
        // Kept for any legacy references; per-type filtering is now done via linkErrorsOnlyByTltId.
        linkErrorsOnlyFilter() {
            if (this.showErrorsOnly) {
                return ['_hasErrors', '=', true]
            }
            return null
        },

        flatLinkRowsByTltId() {
            const grouped = {}
            this.flatLinkRows.forEach(row => {
                const tltId = row._tltId
                if (!grouped[tltId]) grouped[tltId] = []
                grouped[tltId].push(row)
            })
            return grouped
        },

        totalDuplicateDbRows() {
            let count = 0
            Object.values(this.flatRows).forEach(rows => {
                rows.forEach(row => { if (row._isDuplicateDb) count++ })
            })
            return count
        },

        // Step 4: Confirm Import is disabled until every applicable row has a non-null _statusId.
        // Update rows are exempt (status untouched on update). Skipped rows (file dupes / Skip action) are exempt.
        // Error rows are excluded entirely from the import.
        missingStatusRowCount() {
            let missing = 0
            for (const ttyId of Object.keys(this.flatRows)) {
                const rows = this.flatRows[ttyId] || []
                for (const row of rows) {
                    if (Object.keys(row._cellErrors || {}).length > 0) continue
                    if (row._rowAction === 'Update') continue
                    if (row._rowAction === 'Skip') continue
                    if (row._isDuplicateFile && !row._fileGroupSelected) continue
                    if (row._statusId === null || row._statusId === undefined) missing++
                }
            }
            return missing
        },
        confirmDisabled() {
            if (this.isImporting) return true
            // Must have SOMETHING to import — either topic rows (new/update) OR link rows
            const importableLinkCount = (this.flatLinkRows || []).filter(r =>
                Object.keys(r._cellErrors || {}).length === 0 && !r._linkError
            ).length
            if ((this.importSummary.new + this.importSummary.updates + importableLinkCount) === 0) return true
            if (this.missingStatusRowCount > 0) return true
            return false
        },

        // Pitfall 5: intersect status NAMES across every selected topic type's workflow.
        // The displayed list contains only names that exist in EVERY type — bulk apply then
        // resolves the type-specific id per row at apply time.
        bulkStatusOptions() {
            const ttyIds = Object.keys(this.flatRows).filter(t => (this.flatRows[t] || []).length > 0)
            if (ttyIds.length === 0) return []
            let intersect = null
            for (const ttyId of ttyIds) {
                const list = this.statusesByTtyId[ttyId] || []
                const names = new Set(list.map(s => s.name))
                intersect = intersect == null
                    ? names
                    : new Set([...intersect].filter(n => names.has(n)))
            }
            if (!intersect || intersect.size === 0) return []
            // Display names sourced from the first ttyId's bucket (description optional)
            const firstList = this.statusesByTtyId[ttyIds[0]] || []
            const seen = new Set()
            const out = []
            for (const s of firstList) {
                if (intersect.has(s.name) && !seen.has(s.name)) {
                    seen.add(s.name)
                    out.push({ name: s.name })
                }
            }
            return out
        },

        // One accordion item per ttyId carrying its label, ttyId, and the row array reference
        accordionItems() {
            const items = []
            const types = this.importableTypes || []
            for (const ttyId of Object.keys(this.flatRows)) {
                const rows = this.flatRows[ttyId] || []
                if (rows.length === 0) continue
                const numericTtyId = parseInt(ttyId)
                const ttyName = (types.find(t => t.TtyId === numericTtyId) || {}).TtyName
                    || ('Type ' + ttyId)
                items.push({ ttyId: numericTtyId, ttyName, rows })
            }
            return items
        },

        resultTopicRows() {
            if (!this.importResult || !this.importResult.results) return [];
            const types = this.importableTypes || [];
            return this.importResult.results
                .filter(r => r.rowType === 'topic')
                .map(r => ({
                    ...r,
                    ttyName: (types.find(t => t.TtyId === r.ttyId) || {}).TtyName || ('Type ' + r.ttyId)
                }));
        },
        resultLinkRows() {
            if (!this.importResult || !this.importResult.results) return [];
            return this.importResult.results.filter(r => r.rowType === 'link');
        },
        resultSummary() {
            const rows = this.importResult ? (this.importResult.results || []) : [];
            return {
                imported: rows.filter(r => r.status === 'imported').length,
                updated:  rows.filter(r => r.status === 'updated').length,
                skipped:  rows.filter(r => r.status === 'skipped').length,
                errored:  rows.filter(r => r.status === 'error').length
            };
        },
        resultTopicFilter() {
            if (!this.resultStatusFilter) return null;
            return [['status', '=', this.resultStatusFilter]];
        },
        resultLinkFilter() {
            if (!this.resultStatusFilter) return null;
            return [['status', '=', this.resultStatusFilter]];
        }
    },
    created() {
        this.loadImportableTypes();
        // Load link types in parallel — failure must NOT block topic type loading
        this.loadAvailableLinkTypes();
        // Best-effort cleanup if the user closes the tab mid-wizard. sendBeacon survives
        // unload where fetch/XHR usually don't.
        this._beforeUnloadHandler = () => this.cancelUploadIfAbandoned(true);
        window.addEventListener('beforeunload', this._beforeUnloadHandler);
    },
    beforeUnmount() {
        if (this._beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this._beforeUnloadHandler);
        }
        this.cancelUploadIfAbandoned(false);
    },
    beforeRouteLeave(to, from, next) {
        this.cancelUploadIfAbandoned(false);
        next();
    },
    watch: {
        // Defensive: some DxTabPanel navigation paths set currentStepIndex directly
        // without going through goToStep(). Trigger the Step 4 status load from here
        // so the dropdowns never end up empty regardless of how we arrived.
        currentStepIndex(newVal) {
            if (newVal === 3) this.loadAvailableStatuses();
        }
    },
    methods: {
        downloadTemplate() {
            if (this.selectedTtyIds.length === 0) return;
            this.isDownloading = true;
            this.downloadError = null;

            const ttyIdsParam = this.selectedTtyIds.join(',');
            const tltIdsParam = (this.selectedTltIds || []).join(',');
            let url = 'WebApi.asmx/api/topicImportService/GetTemplate?ttyIds=' + ttyIdsParam;
            if (tltIdsParam) url += '&tltIds=' + tltIdsParam;

            // MUST be synchronous from click — no await before window.open (pop-up blocker pitfall)
            window.open(url, '_blank');

            // window.open does not return a Promise — clear spinner after browser handoff delay
            setTimeout(() => {
                this.isDownloading = false;
            }, 1500);
        },

        goToStep(index) {
            if (index > this.currentStepIndex && this.currentStepIndex > this.highestCompletedStep) {
                this.highestCompletedStep = this.currentStepIndex;
            }
            this.currentStepIndex = index;
            // The currentStepIndex watcher triggers loadAvailableStatuses on step 3 — do
            // NOT call it here too. Double-firing re-runs initDefaultStatusesPerRow
            // and clobbers per-row statuses the user already picked when they revisit
            // the step.
        },

        stepTitle(stepIndex, label) {
            if (stepIndex <= this.highestCompletedStep) {
                return '✓ ' + label;
            }
            return label;
        },

        onFileSelected(e) {
            this.selectedFile = e.value && e.value.length > 0 ? e.value[0] : null;
        },

        async uploadAndParse() {
            this.isParsing = true;
            this.parseError = null;

            try {
                // Step 1: Upload
                const formData = new FormData();
                formData.append('file', this.selectedFile);

                const uploadRes = await axios.post(
                    'WebApi.asmx/api/topicImportService/Upload',
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (!uploadRes.data.success) {
                    this.parseError = uploadRes.data.errorMessage || 'Upload failed.';
                    this.isParsing = false;
                    return;
                }

                this.fileId = uploadRes.data.fileId;

                // Step 2: Parse
                const parseRes = await axios.get(
                    'WebApi.asmx/api/topicImportService/Parse?fileId=' + this.fileId
                );

                if (!parseRes.data.success) {
                    this.parseError = parseRes.data.errorMessage || 'Parse failed.';
                    this.isParsing = false;
                    return;
                }

                this.parseResult = parseRes.data;
                this.buildFlatRows(parseRes.data);
                this.isParsing = false;
                this.goToStep(2);

            } catch (err) {
                this.parseError = (err && err.message) ? err.message : 'An unexpected error occurred.';
                this.isParsing = false;
            }
        },

        buildFlatRows(parseResult) {
            this.sheetTabs = [];
            this.flatRows = {};
            this.flatLinkRows = [];

            // Build per-sheet flat rows
            for (const [ttyIdStr, sheet] of Object.entries(parseResult.sheets || {})) {
                const ttyId = parseInt(ttyIdStr);

                this.sheetTabs.push({
                    ttyId,
                    sheetName: sheet.sheetName,
                    columns: sheet.columns || []
                });

                // Track seen importKeys for file duplicate group selection
                const seenImportKeys = new Set();

                const rows = (sheet.rows || []).map(row => {
                    // File duplicate: first occurrence gets _fileGroupSelected=true, subsequent=false
                    // Group by duplicateGroup (Import Key after fix), fallback to importKey if duplicateGroup is null
                    let fileGroupSelected = true;
                    const groupKey = row.duplicateGroup || row.importKey;
                    if (row.isDuplicateFile && groupKey) {
                        if (seenImportKeys.has(groupKey)) {
                            fileGroupSelected = false;
                        } else {
                            seenImportKeys.add(groupKey);
                        }
                    }

                    const flat = {
                        _rowIndex: row.rowIndex,
                        _ttyId: parseInt(ttyId),
                        _importKey: row.importKey,
                        _hasErrors: row.hasErrors,
                        _isDuplicateDb: row.isDuplicateDb,
                        _isDuplicateFile: row.isDuplicateFile,
                        _duplicateGroup: row.duplicateGroup || null,
                        _existingTopicId: row.existingTopicId,
                        _rowAction: row.isDuplicateDb ? 'Skip' : (row.isDuplicateFile && !fileGroupSelected ? 'Skip' : 'New'),
                        _fileGroupSelected: row.isDuplicateFile ? fileGroupSelected : true,
                        _cellErrors: {},
                        _resolvedValues: {}
                    };

                    for (const cell of (row.cells || [])) {
                        // Display the label (originalValue) in the grid; keep numeric ID in _resolvedValues for backend payload
                        flat[cell.fieldName] = cell.originalValue;
                        if (cell.resolvedValue !== null && cell.resolvedValue !== undefined) {
                            flat._resolvedValues[cell.fieldName] = cell.resolvedValue;
                        }

                        if (cell.error) {
                            flat._cellErrors[cell.fieldName] = cell.error.message;
                        }
                    }

                    return flat;
                });

                this.flatRows[ttyId] = rows;
            }

            // Build link columns for validation grid from backend response — preserve full metadata for inline editing
            this.linkColumns = (parseResult.linkColumns || []).map(col => ({
                fieldName: col.fieldName,
                caption: col.caption,
                matId: col.matId,
                atyId: col.atyId,
                isListItem: col.isListItem,
                isRequired: col.isRequired
            }));

            // Build per-type column definitions for split link grid tabs
            const byTltId = {}
            if (parseResult.linkColumnsByTltId) {
                for (const tltIdStr of Object.keys(parseResult.linkColumnsByTltId)) {
                    const entry = parseResult.linkColumnsByTltId[tltIdStr]
                    byTltId[tltIdStr] = {
                        tltId: entry.tltId,
                        linkTypeName: entry.linkTypeName,
                        columns: (entry.columns || []).map(col => ({
                            fieldName: col.fieldName,
                            caption: col.caption,
                            matId: col.matId,
                            atyId: col.atyId,
                            isListItem: col.isListItem,
                            isRequired: col.isRequired
                        }))
                    }
                }
            }
            this.linkColumnsByTltId = byTltId
            this.linkErrorsOnlyByTltId = {}

            // Build flat link rows
            this.flatLinkRows = (parseResult.linkRows || []).map(row => {
                const flat = {
                    _rowIndex: row.rowIndex,
                    _hasErrors: row.hasErrors,
                    _tltId: row.tltId,
                    _sourceImportKey: row.sourceImportKey,
                    _targetImportKey: row.targetImportKey,
                    _sourceTopicId: row.sourceTopicId || null,
                    _targetTopicId: row.targetTopicId || null,
                    _rowAction: null,  // null = Import, 'Skip' = Skip
                    _linkError: row.linkError || null,
                    _cellErrors: {},
                    _resolvedValues: {}
                };

                for (const cell of (row.cells || [])) {
                    flat[cell.fieldName] = cell.originalValue;
                    if (cell.resolvedValue !== null && cell.resolvedValue !== undefined) {
                        flat._resolvedValues[cell.fieldName] = cell.resolvedValue;
                    }

                    if (cell.error) {
                        flat._cellErrors[cell.fieldName] = cell.error.message;
                    }
                }

                return flat;
            });
        },

        hasSheetDuplicates(ttyId) {
            const rows = this.flatRows[ttyId] || [];
            return rows.some(r => r._isDuplicateDb === true || r._isDuplicateFile === true);
        },

        onCellPrepared(e) {
            if (e.rowType !== 'data') return;

            // Row-level duplicate coloring
            const tr = e.cellElement.closest ? e.cellElement.closest('tr') : null;
            if (tr) {
                if (e.data._isDuplicateFile) {
                    tr.classList.add('import-row-dup-file');
                } else if (e.data._isDuplicateDb) {
                    tr.classList.add('import-row-dup-db');
                }
            }

            // Cell-level error coloring + inline error message
            const fieldName = e.column.dataField;
            // DevExtreme refires cellPrepared on virtual scroll, repaint, and rowUpdating — strip
            // any prior error node first so messages don't accumulate or persist after a fix.
            const stale = e.cellElement.querySelector(':scope > .import-cell-error');
            if (stale) stale.remove();
            if (fieldName && e.data._cellErrors && e.data._cellErrors[fieldName]) {
                e.cellElement.style.backgroundColor = '#ffd7d7';
                e.cellElement.title = e.data._cellErrors[fieldName];
                const errSpan = document.createElement('div');
                errSpan.className = 'import-cell-error';
                errSpan.textContent = e.data._cellErrors[fieldName];
                e.cellElement.appendChild(errSpan);
            } else if (fieldName) {
                e.cellElement.style.backgroundColor = '';
                e.cellElement.title = '';
            }
        },

        onLinkCellPrepared(e) {
            if (e.rowType !== 'data') return;

            // Row-level link error coloring
            const tr = e.cellElement.closest ? e.cellElement.closest('tr') : null;
            if (tr && e.data._linkError) {
                tr.classList.add('import-row-dup-file');
            }

            const fieldName = e.column.dataField;

            // Strip any stale error node first so repaints don't stack duplicates or
            // leave a fixed cell still showing the prior error.
            const stale = e.cellElement.querySelector(':scope > .import-cell-error');
            if (stale) stale.remove();

            // Surface the row-level _linkError message on the Source/Target ImportKey cells
            // so the user can see WHICH key is wrong and edit it inline.
            let appended = false;
            if (e.data._linkError
                && (fieldName === '_sourceImportKey' || fieldName === '_targetImportKey')) {
                const le = e.data._linkError;
                const linkErrText = typeof le === 'string' ? le : (le && le.message) || 'Invalid link';
                e.cellElement.style.backgroundColor = '#ffd7d7';
                e.cellElement.title = linkErrText;
                const errSpan = document.createElement('div');
                errSpan.className = 'import-cell-error';
                errSpan.textContent = linkErrText;
                e.cellElement.appendChild(errSpan);
                appended = true;
            }

            // Cell-level error coloring + inline error message
            if (!appended && fieldName && e.data._cellErrors && e.data._cellErrors[fieldName]) {
                e.cellElement.style.backgroundColor = '#ffd7d7';
                e.cellElement.title = e.data._cellErrors[fieldName];
                const errSpan = document.createElement('div');
                errSpan.className = 'import-cell-error';
                errSpan.textContent = e.data._cellErrors[fieldName];
                e.cellElement.appendChild(errSpan);
                appended = true;
            }

            if (!appended && fieldName) {
                e.cellElement.style.backgroundColor = '';
                e.cellElement.title = '';
            }
        },

        // Link grid inline editing — per-cell readOnly control (mirrors onEditorPreparing for topic grids)
        onLinkEditorPreparing(e) {
            if (e.parentType !== 'dataRow') return;
            const row = e.row.data;
            const fieldName = e.dataField;

            // Source/Target ImportKey — editable only when the row has a linkError
            // (e.g. "Import Key X not found in any topic sheet"). Otherwise read-only.
            if (fieldName === '_sourceImportKey' || fieldName === '_targetImportKey') {
                if (!row._linkError) e.editorOptions.readOnly = true;
                return;
            }

            // Only error cells are editable — valid cells remain read-only (matches topic grid behavior)
            if (!row._cellErrors || !row._cellErrors[fieldName]) {
                e.editorOptions.readOnly = true;
                return;
            }

            // Look up colMeta from this row's per-type column list. Do NOT fall back to
            // the flat linkColumns list — that returns the wrong type's metadata and can
            // mislead the editor (e.g. wrong dropdown options). If the entry is missing,
            // leave the cell in the default editor.
            const tltIdKey = String(row._tltId)
            const typeEntry = this.linkColumnsByTltId[tltIdKey]
            const colMeta = typeEntry && typeEntry.columns
                ? typeEntry.columns.find(c => c.fieldName === fieldName)
                : null;
            if (!colMeta) return;

            if (colMeta.isListItem) {
                const optionsKey = row._tltId + '_' + fieldName;
                const options = (this.parseResult && this.parseResult.linkListItemOptions)
                    ? this.parseResult.linkListItemOptions[optionsKey] || []
                    : [];
                e.editorName = 'dxSelectBox';
                e.editorOptions.dataSource = options;
                e.editorOptions.valueExpr = 'label';
                e.editorOptions.displayExpr = 'label';
                e.editorOptions.searchEnabled = true;
            } else if (colMeta.atyId === 4) {
                e.editorName = 'dxDateBox';
                e.editorOptions.type = 'date';
                e.editorOptions.displayFormat = 'dd.MM.yyyy';
            } else if (colMeta.atyId === 2 || colMeta.atyId === 13) {
                e.editorName = 'dxNumberBox';
            }
        },

        // Link grid client-side re-validation after cell edit (mirrors onRowUpdating for topic grids)
        // tltId is passed from the per-type grid's @row-updating binding so we repaint the correct grid ref
        onLinkRowUpdating(e, tltId) {
            const row = e.oldData;
            const fieldName = Object.keys(e.newData)[0];
            const newValue = e.newData[fieldName];

            // Look up colMeta from this row's per-type column list — never fall back to
            // the flat list, which returns wrong-type metadata and produces misleading
            // validation errors.
            const tltIdKey = String(tltId !== undefined ? tltId : row._tltId)
            const typeEntry = this.linkColumnsByTltId[tltIdKey]
            const colMeta = typeEntry && typeEntry.columns
                ? typeEntry.columns.find(c => c.fieldName === fieldName)
                : null;

            let errorMsg = null;
            if (colMeta) {
                if (colMeta.isRequired && (newValue === null || newValue === undefined || newValue === '')) {
                    errorMsg = `'${colMeta.caption}' is required`;
                } else if (colMeta.isListItem && newValue) {
                    const optionsKey = row._tltId + '_' + fieldName;
                    const options = (this.parseResult && this.parseResult.linkListItemOptions)
                        ? this.parseResult.linkListItemOptions[optionsKey] || []
                        : [];
                    if (!options.find(o => o.label === newValue)) {
                        errorMsg = `'${colMeta.caption}': value '${newValue}' not found in list`;
                    }
                } else if ((colMeta.atyId === 2 || colMeta.atyId === 13) && newValue !== null && newValue !== '' && isNaN(Number(newValue))) {
                    errorMsg = `'${colMeta.caption}' must be a number`;
                }
            }

            const rowIndex = this.flatLinkRows.indexOf(row);
            if (rowIndex < 0) return;

            const updatedCellErrors = { ...row._cellErrors };
            if (errorMsg) updatedCellErrors[fieldName] = errorMsg;
            else delete updatedCellErrors[fieldName];

            // Re-validate row-level _linkError when the user edits an ImportKey identity field.
            // Collect all topic-row import keys (case-insensitive) and re-check both sides.
            let updatedLinkError = row._linkError;
            if (fieldName === '_sourceImportKey' || fieldName === '_targetImportKey') {
                const allImportKeys = new Set();
                for (const ttyId of Object.keys(this.flatRows)) {
                    for (const r of (this.flatRows[ttyId] || [])) {
                        if (r._importKey) allImportKeys.add(String(r._importKey).toLowerCase());
                    }
                }
                const srcKey = (fieldName === '_sourceImportKey' ? newValue : row._sourceImportKey) || '';
                const tgtKey = (fieldName === '_targetImportKey' ? newValue : row._targetImportKey) || '';
                const srcTopicId = row._sourceTopicId;
                const tgtTopicId = row._targetTopicId;
                const srcResolved = (srcTopicId && srcTopicId > 0) || allImportKeys.has(String(srcKey).toLowerCase());
                const tgtResolved = (tgtTopicId && tgtTopicId > 0) || allImportKeys.has(String(tgtKey).toLowerCase());
                if (!srcResolved && !srcKey) {
                    updatedLinkError = "Source Import Key (or Source Topic ID) is required";
                } else if (!tgtResolved && !tgtKey) {
                    updatedLinkError = "Target Import Key (or Target Topic ID) is required";
                } else if (!srcResolved) {
                    updatedLinkError = `Source Import Key '${srcKey}' not found in any topic sheet`;
                } else if (!tgtResolved) {
                    updatedLinkError = `Target Import Key '${tgtKey}' not found in any topic sheet`;
                } else {
                    updatedLinkError = null;
                }
            }

            // Resolve list-item label → ID for _resolvedValues
            const updatedResolvedValues = { ...(row._resolvedValues || {}) };
            if (colMeta && colMeta.isListItem && newValue) {
                const optionsKey = row._tltId + '_' + fieldName;
                const options = (this.parseResult && this.parseResult.linkListItemOptions)
                    ? this.parseResult.linkListItemOptions[optionsKey] || []
                    : [];
                const match = options.find(o => o.label === newValue);
                if (match) updatedResolvedValues[fieldName] = match.value;
            } else if (colMeta && !colMeta.isListItem) {
                updatedResolvedValues[fieldName] = newValue;
            }

            const updatedRow = {
                ...row,
                ...e.newData,
                _cellErrors: updatedCellErrors,
                _linkError: updatedLinkError,
                _hasErrors: Object.keys(updatedCellErrors).length > 0 || !!updatedLinkError,
                _resolvedValues: updatedResolvedValues,
            };
            this.flatLinkRows.splice(rowIndex, 1, updatedRow);

            e.cancel = true;

            this.$nextTick(() => {
                const refKey = 'linkGrid_' + (tltId !== undefined ? tltId : row._tltId);
                const gridRef = this.$refs[refKey];
                const gridInstance = gridRef && (Array.isArray(gridRef) ? gridRef[0] : gridRef);
                if (gridInstance && gridInstance.instance) gridInstance.instance.repaint();
            });
        },

        // Per-type helpers for split link grid tabs

        getLinkTabTitle(tltIdStr) {
            const entry = (this.linkColumnsByTltId || {})[tltIdStr];
            const name = entry ? entry.linkTypeName : ('Link Type ' + tltIdStr);
            const rows = (this.flatLinkRowsByTltId || {})[parseInt(tltIdStr)] || [];
            return name + ' (' + rows.length + ')';
        },

        linkTypeErrorCount(tltId) {
            const rows = this.flatLinkRowsByTltId[tltId] || [];
            return rows.filter(r => r._hasErrors || !!r._linkError).length;
        },

        filteredLinkRowsByTltId(tltId) {
            const rows = this.flatLinkRowsByTltId[tltId] || [];
            const errorsOnly = this.linkErrorsOnlyByTltId[String(tltId)];
            if (errorsOnly) {
                return rows.filter(r => r._hasErrors || !!r._linkError);
            }
            return rows;
        },

        toggleLinkErrorFilter(tltIdStr, value) {
            this.linkErrorsOnlyByTltId = { ...this.linkErrorsOnlyByTltId, [tltIdStr]: value };
        },

        // Task 1: Inline cell editing — per-cell readOnly control
        onEditorPreparing(e) {
            if (e.parentType !== 'dataRow') return;
            const row = e.row.data;
            const fieldName = e.dataField;

            // Action column always handled by cell template (not inline editing)
            if (fieldName === '_rowAction' || fieldName === '_importKey') {
                e.editorOptions.readOnly = true;
                return;
            }

            // Only error cells are editable — valid cells remain read-only
            if (!row._cellErrors || !row._cellErrors[fieldName]) {
                e.editorOptions.readOnly = true;
                return;
            }

            // Find column metadata to determine editor type
            const tab = this.sheetTabs.find(t => {
                const rows = this.flatRows[t.ttyId];
                return rows && rows.includes(row);
            });
            if (!tab) return;
            const colMeta = tab.columns.find(c => c.fieldName === fieldName);
            if (!colMeta) return;

            // ListItem fields: configure as DxSelectBox with valid values
            if (colMeta.isListItem) {
                const optionsKey = tab.ttyId + '_' + fieldName;
                const options = (this.parseResult && this.parseResult.listItemOptions)
                    ? this.parseResult.listItemOptions[optionsKey] || []
                    : [];
                e.editorName = 'dxSelectBox';
                e.editorOptions.items = options;
                e.editorOptions.valueExpr = 'label';  // Store the label; backend re-resolves on import
                e.editorOptions.displayExpr = 'label';
                e.editorOptions.searchEnabled = true;
            }
            // Date fields (atyId 4 = Date): use dxDateBox
            else if (colMeta.atyId === 4) {
                e.editorName = 'dxDateBox';
                e.editorOptions.type = 'date';
                e.editorOptions.displayFormat = 'dd.MM.yyyy';
            }
            // Number fields (atyId 2 = Integer, 13 = Decimal): use dxNumberBox
            else if (colMeta.atyId === 2 || colMeta.atyId === 13) {
                e.editorName = 'dxNumberBox';
            }
            // Text fields: default dxTextBox is fine
        },

        // Task 1: Client-side re-validation after cell edit
        onRowUpdating(e) {
            const row = e.oldData;
            const fieldName = Object.keys(e.newData)[0];
            const newValue = e.newData[fieldName];

            // Find column metadata
            const tab = this.sheetTabs.find(t => {
                const rows = this.flatRows[t.ttyId];
                return rows && rows.includes(row);
            });
            if (!tab) return;
            const colMeta = tab.columns.find(c => c.fieldName === fieldName);

            // Client-side re-validation
            let errorMsg = null;
            if (colMeta) {
                // Required check
                if (colMeta.isRequired && (newValue === null || newValue === undefined || newValue === '')) {
                    errorMsg = `'${colMeta.caption}' is required`;
                }
                // ListItem resolution check
                else if (colMeta.isListItem && newValue) {
                    const optionsKey = tab.ttyId + '_' + fieldName;
                    const options = (this.parseResult && this.parseResult.listItemOptions)
                        ? this.parseResult.listItemOptions[optionsKey] || []
                        : [];
                    if (!options.find(o => o.label === newValue)) {
                        errorMsg = `'${colMeta.caption}': value '${newValue}' not found in list`;
                    }
                }
                // Type checks for numbers
                else if ((colMeta.atyId === 2 || colMeta.atyId === 13) && newValue !== null && newValue !== '' && isNaN(Number(newValue))) {
                    errorMsg = `'${colMeta.caption}' must be a number`;
                }
            }

            // Update error state — MUST use splice to trigger Vue 3 reactivity
            const ttyId = tab.ttyId;
            const rows = this.flatRows[ttyId];
            const rowIndex = rows.indexOf(row);
            const updatedCellErrors = { ...row._cellErrors };
            if (errorMsg) {
                updatedCellErrors[fieldName] = errorMsg;
            } else {
                delete updatedCellErrors[fieldName];
            }
            // Resolve list-item label → ID for _resolvedValues
            const updatedResolvedValues = { ...(row._resolvedValues || {}) };
            if (colMeta && colMeta.isListItem && newValue) {
                const optionsKey = tab.ttyId + '_' + fieldName;
                const options = (this.parseResult && this.parseResult.listItemOptions)
                    ? this.parseResult.listItemOptions[optionsKey] || []
                    : [];
                const match = options.find(o => o.label === newValue);
                if (match) updatedResolvedValues[fieldName] = match.value;
            } else if (colMeta && !colMeta.isListItem) {
                updatedResolvedValues[fieldName] = newValue;
            }

            const updatedRow = {
                ...row,
                ...e.newData,
                _cellErrors: updatedCellErrors,
                _hasErrors: Object.keys(updatedCellErrors).length > 0,
                _resolvedValues: updatedResolvedValues,
            };
            rows.splice(rowIndex, 1, updatedRow);

            // Cancel default DxDataGrid update — we already replaced the row object above
            e.cancel = true;

            // Force grid repaint to update cell styling
            this.$nextTick(() => {
                const gridRef = this.$refs['grid_' + ttyId];
                const gridInstance = gridRef && (Array.isArray(gridRef) ? gridRef[0] : gridRef);
                if (gridInstance && gridInstance.instance) {
                    gridInstance.instance.repaint();
                }
            });
        },

        // Task 2: Per-row duplicate action change — replace inner array so DxDataGrid rebinds cell templates
        onRowActionChanged(row, ttyId, e) {
            const targetKey = row._rowIndex;
            this.flatRows = {
                ...this.flatRows,
                [ttyId]: this.flatRows[ttyId].map(r =>
                    r._rowIndex === targetKey ? { ...r, _rowAction: e.value } : r
                )
            };
        },

        // Task 2: Bulk duplicate action — replace inner arrays so DxDataGrid rebinds cell templates
        bulkDuplicateAction(action) {
            const updated = {};
            Object.keys(this.flatRows).forEach(ttyId => {
                updated[ttyId] = this.flatRows[ttyId].map(row =>
                    row._isDuplicateDb ? { ...row, _rowAction: action } : row
                );
            });
            this.flatRows = updated;
        },

        // Task 2: File duplicate group selection — replace inner array so DxDataGrid rebinds cell templates
        toggleFileDuplicate(row, ttyId) {
            const group = row._duplicateGroup;
            const targetKey = row._rowIndex;
            this.flatRows = {
                ...this.flatRows,
                [ttyId]: this.flatRows[ttyId].map(r => {
                    if (!r._isDuplicateFile || r._duplicateGroup !== group) return r;
                    return r._rowIndex === targetKey
                        ? { ...r, _fileGroupSelected: true, _rowAction: 'New' }
                        : { ...r, _fileGroupSelected: false, _rowAction: 'Skip' };
                })
            };
        },

        // Step 4: load per-workflow statuses keyed by ttyId (Plan 10-01 contract)
        // Response shape: { success, data: { statusesByTtyId: { "1": [{id,name,description,isInitial,staOrder}], ... } } }
        async loadAvailableStatuses() {
            // Primary source: topic types the user picked in Step 1.
            // Fallback: parsed topic sheets (user may have skipped Step 1 by re-using an existing
            // template and jumping straight to upload — in that case selectedTtyIds stays empty
            // but flatRows is populated with the sheets the Meta sheet identified).
            let ttyIdArr = (this.selectedTtyIds || []).slice();
            if (ttyIdArr.length === 0) {
                ttyIdArr = Object.keys(this.flatRows || {}).map(k => parseInt(k)).filter(n => !isNaN(n));
            }
            const ttyIds = ttyIdArr.join(',');
            if (!ttyIds) return;
            try {
                const res = await axios.get(
                    'WebApi.asmx/api/topicImportService/GetStatuses?ttyIds=' + ttyIds
                )
                const map = (res.data && res.data.data && res.data.data.statusesByTtyId) || {}
                this.statusesByTtyId = map
                this.initDefaultStatusesPerRow()
            } catch (err) {
                console.error('Failed to load statuses', err)
            }
        },

        // Assign each non-Update, non-Skip row a default _statusId based on the topic type's
        // workflow initial status (isInitial === true), falling back to the first by staOrder
        // (the backend already orders the list). Preserves any _statusId the user has already
        // picked so revisiting Step 4 does not clobber selections.
        initDefaultStatusesPerRow() {
            const updated = {}
            for (const ttyId of Object.keys(this.flatRows)) {
                const list = this.statusesByTtyId[ttyId] || []
                const initial = list.find(s => s.isInitial) || list[0]
                const defaultId = initial ? initial.id : null
                const rows = (this.flatRows[ttyId] || []).map(row => {
                    // Update rows: do NOT set _statusId — Update rows show existing status read-only
                    if (row._rowAction === 'Update') return { ...row, _statusId: null }
                    // Unselected file duplicates: skipped — backend ignores
                    if (row._isDuplicateFile && !row._fileGroupSelected) return { ...row, _statusId: null }
                    // Skip rows: leave unset
                    if (row._rowAction === 'Skip') return { ...row, _statusId: null }
                    // Preserve a user-picked status; only seed the default when no choice was made.
                    if (row._statusId !== null && row._statusId !== undefined) return row
                    return { ...row, _statusId: defaultId }
                })
                updated[ttyId] = rows
            }
            this.flatRows = updated
        },

        // Bulk apply: when admin picks a status by NAME, walk every flat row and assign the
        // type-specific status id (Pitfall 5: intersect by name, apply per-type id).
        // Update / Skip / unselected file-duplicate rows are left untouched.
        onBulkStatusChanged(e) {
            const name = e.value
            if (!name) return
            const updated = {}
            for (const ttyId of Object.keys(this.flatRows)) {
                const list = this.statusesByTtyId[ttyId] || []
                const match = list.find(s => s.name === name)
                const rows = (this.flatRows[ttyId] || []).map(row => {
                    if (row._rowAction === 'Update') return row
                    if (row._rowAction === 'Skip') return row
                    if (row._isDuplicateFile && !row._fileGroupSelected) return row
                    if (!match) return row
                    return { ...row, _statusId: match.id }
                })
                updated[ttyId] = rows
            }
            this.flatRows = updated
            this.bulkIsMixed = false
        },

        // Per-row status DxSelectBox change handler — once any row diverges, the bulk helper
        // displays "(mixed)" so the admin knows the helper no longer reflects every row.
        onRowStatusChanged() {
            this.bulkIsMixed = true
        },

        // Returns the status list for the given flat row's topic type.
        // Using a method (rather than a direct dict lookup in the template) ensures Vue
        // re-evaluates on every render and avoids DevExtreme cellTemplate reactivity gaps.
        getStatusListForRow(row) {
            return (row && row._ttyId !== undefined && this.statusesByTtyId[row._ttyId]) || []
        },

        // Tell the backend to discard the temp .xlsx if the user navigates away or closes
        // the tab without finishing the import. Without this, abandoned uploads accumulate
        // in the IIS temp folder until the 24h sweep on next Upload reaps them.
        cancelUploadIfAbandoned(useBeacon) {
            if (!this.fileId || this.importResult) return;
            const url = 'WebApi.asmx/api/topicImportService/Cancel';
            const fd = new FormData();
            fd.append('fileId', this.fileId);
            try {
                if (useBeacon && navigator && typeof navigator.sendBeacon === 'function') {
                    navigator.sendBeacon(url, fd);
                } else {
                    // fire-and-forget; don't block navigation on the response
                    axios.post(url, fd).catch(() => { /* best effort */ });
                }
            } catch (e) { /* best effort — never block navigation */ }
        },

        // Step 1: load importable topic types. Surfaces failures so the user sees an
        // explanation instead of an empty TagBox with no diagnostic.
        async loadImportableTypes() {
            this.importableTypesError = null
            try {
                const res = await axios.get('WebApi.asmx/api/topicImportService/GetImportableTypes')
                if (res && res.data && res.data.success) {
                    this.importableTypes = res.data.data || []
                } else {
                    this.importableTypes = []
                    this.importableTypesError = (res && res.data && res.data.errorMessage) || 'Failed to load topic types'
                }
            } catch (err) {
                this.importableTypes = []
                this.importableTypesError = (err && err.message) ? err.message : 'Failed to load topic types'
                console.error('Failed to load importable types', err)
            }
        },

        // Step 1: load available link types for the optional Link Types DxTagBox.
        // Failure does NOT block topic type loading or wizard progression.
        async loadAvailableLinkTypes() {
            this.isLoadingLinkTypes = true
            this.linkTypesError = null
            try {
                const res = await axios.get('WebApi.asmx/api/topicImportService/GetAvailableLinkTypes')
                if (res.data && res.data.success) {
                    this.availableLinkTypes = res.data.data || []
                } else {
                    this.availableLinkTypes = []
                    this.linkTypesError = (res.data && res.data.errorMessage) || 'Unknown error'
                }
            } catch (err) {
                this.availableLinkTypes = []
                this.linkTypesError = (err && err.message) ? err.message : 'Failed to load link types'
                console.error('Failed to load link types', err)
            } finally {
                this.isLoadingLinkTypes = false
            }
        },

        // Step 4: build rowActions payload from flatRows — excludes error rows, skips unselected file dupes
        buildRowActionsPayload() {
            const rowActions = []
            for (const ttyId of Object.keys(this.flatRows)) {
                const rows = this.flatRows[ttyId] || []
                for (const row of rows) {
                    // Skip rows with errors — they are excluded from import
                    if (Object.keys(row._cellErrors || {}).length > 0) continue
                    // File duplicates not selected are skipped
                    if (row._isDuplicateFile && !row._fileGroupSelected) {
                        rowActions.push({ importKey: row._importKey, ttyId: parseInt(ttyId), statusId: null, action: 'Skip', existingTopicId: null, cells: [] })
                        continue
                    }
                    rowActions.push({
                        importKey: row._importKey,
                        ttyId: parseInt(ttyId),
                        // Per-row statusId (Plan 10-04). Backend (10-03 validation) only enforces for action == 'New'.
                        // Update / Skip rows send null — status untouched on update.
                        statusId: (row._rowAction === 'Update' || row._rowAction === 'Skip') ? null : (row._statusId || null),
                        action: row._rowAction || 'New',
                        existingTopicId: row._existingTopicId || null,
                        cells: (this.parseResult && this.parseResult.sheets && this.parseResult.sheets[ttyId])
                            ? (this.parseResult.sheets[ttyId].rows || [])
                                .filter(pr => pr.rowIndex === row._rowIndex)
                                .flatMap(pr => (pr.cells || []).map(c => ({
                                    fieldName: c.fieldName,
                                    matId: c.matId,
                                    atyId: c.atyId,
                                    resolvedValue: (row._resolvedValues && row._resolvedValues[c.fieldName] !== undefined) ? row._resolvedValues[c.fieldName] : (row[c.fieldName] !== undefined ? row[c.fieldName] : c.resolvedValue)
                                })))
                            : []
                    })
                }
            }
            return rowActions
        },

        // Step 4: build linkRowActions payload from flatLinkRows
        buildLinkActionsPayload() {
            return (this.flatLinkRows || []).map((lr, idx) => {
                // Use the per-link-type column set so cells emit only attributes that
                // actually belong to this row's link type. Iterating the flat
                // `linkColumns` list across all selected types persists values to
                // foreign attributes when ≥2 link types are imported in the same batch.
                // Hard-fail if the row's _tltId is missing or unknown — falling back to the
                // flat linkColumns reintroduces the cross-link-type cell leak this fix exists
                // to prevent. Better to send no cells (backend will treat as no overrides)
                // and surface the bug than to silently corrupt the import.
                const entry = lr._tltId != null
                    ? (this.linkColumnsByTltId || {})[String(lr._tltId)]
                    : null;
                if (!entry) {
                    console.warn('buildLinkActionsPayload: missing tltId entry for row', lr);
                }
                const cols = (entry && entry.columns) || [];
                return {
                    rowIndex: idx,
                    tltId: lr._tltId,
                    action: lr._rowAction === 'Skip' ? 'Skip' : 'Import',
                    sourceImportKey: lr._sourceImportKey,
                    targetImportKey: lr._targetImportKey,
                    sourceTopicId: lr._sourceTopicId || null,
                    targetTopicId: lr._targetTopicId || null,
                    cells: cols.map(c => ({
                        fieldName: c.fieldName,
                        matId: c.matId,
                        atyId: c.atyId,
                        resolvedValue: (lr._resolvedValues && lr._resolvedValues[c.fieldName] !== undefined) ? lr._resolvedValues[c.fieldName] : (lr[c.fieldName] !== undefined ? lr[c.fieldName] : null)
                    }))
                };
            });
        },

        // Step 5: result row coloring
        onResultCellPrepared(e) {
            if (e.rowType !== 'data') return;
            const tr = e.cellElement.closest ? e.cellElement.closest('tr') : null;
            if (!tr) return;
            switch (e.data.status) {
                case 'imported': tr.classList.add('import-row-result-imported'); break;
                case 'updated':  tr.classList.add('import-row-result-updated');  break;
                case 'skipped':  tr.classList.add('import-row-result-skipped');  break;
                case 'error':    tr.classList.add('import-row-result-error');    break;
            }
        },

        // Step 5: reset wizard to Step 1
        resetWizard() {
            this.importResult = null;
            this.importedAt = null;
            this.fileId = null;
            this.parseResult = null;
            this.flatRows = {};
            this.flatLinkRows = [];
            this.sheetTabs = [];
            this.selectedFile = null;
            this.selectedTtyIds = [];
            this.selectedTltIds = [];
            this.statusesByTtyId = {};
            this.bulkStatusName = null;
            this.bulkIsMixed = false;
            this.resultStatusFilter = null;
            this.highestCompletedStep = -1;
            this.currentStepIndex = 0;
        },

        // Step 5: export results
        async exportResults() {
            const workbook = new ExcelJS.Workbook();

            // Sheet 1: Topics — temporarily clear filter to export ALL rows
            const topicsGrid = this.$refs.resultTopicsGrid;
            const topicsInst = topicsGrid && (Array.isArray(topicsGrid) ? topicsGrid[0] : topicsGrid);
            if (topicsInst && topicsInst.instance) {
                const savedFilter = topicsInst.instance.option('filterValue');
                topicsInst.instance.option('filterValue', null);
                const topicsSheet = workbook.addWorksheet('Topics');
                await exportDataGrid({
                    component: topicsInst.instance,
                    worksheet: topicsSheet
                });
                topicsInst.instance.option('filterValue', savedFilter);
            }

            // Sheet 2: Links (only if link rows exist)
            if (this.resultLinkRows.length > 0) {
                const linksGrid = this.$refs.resultLinksGrid;
                const linksInst = linksGrid && (Array.isArray(linksGrid) ? linksGrid[0] : linksGrid);
                if (linksInst && linksInst.instance) {
                    const savedFilter = linksInst.instance.option('filterValue');
                    linksInst.instance.option('filterValue', null);
                    const linksSheet = workbook.addWorksheet('Links');
                    await exportDataGrid({
                        component: linksInst.instance,
                        worksheet: linksSheet
                    });
                    linksInst.instance.option('filterValue', savedFilter);
                }
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const batchLabel = (this.importResult && this.importResult.batchId > 0)
                ? this.importResult.batchId
                : 'Unknown';
            saveAs(
                new Blob([buffer], { type: 'application/octet-stream' }),
                'ImportResult_Batch' + batchLabel + '.xlsx'
            );
        },

        // Step 4: confirm and execute the import
        async confirmImport() {
            this.isImporting = true
            this.importError = null
            try {
                const payload = {
                    fileId: this.fileId,
                    rowActions: this.buildRowActionsPayload(),
                    linkRowActions: this.buildLinkActionsPayload()
                }
                const res = await axios.post('WebApi.asmx/api/topicImportService/Execute', payload,
                    { headers: { 'Content-Type': 'application/json' } })
                if (!res.data || res.data.success !== true) {
                    this.importError = (res.data && res.data.errorMessage) || 'Import failed'
                    return
                }
                this.importResult = res.data
                this.importedAt = new Date()
                // Advance to Step 5 (Results)
                this.highestCompletedStep = 3
                this.currentStepIndex = 4
            } catch (err) {
                this.importError = (err.response && err.response.data && err.response.data.errorMessage)
                    ? err.response.data.errorMessage
                    : (err.message || 'Import failed')
            } finally {
                this.isImporting = false
            }
        }
    }
}
</script>

<style scoped>
.import-row-dup-db td { background-color: #fff3cd !important; }
.import-row-dup-file td { background-color: #ffe0b2 !important; }
.import-summary-bar { display: flex; gap: 12px; margin-bottom: 12px; }
.import-badge { padding: 4px 12px; border-radius: 4px; font-weight: 600; font-size: 0.9em; }
.import-badge-new { background: #d4edda; color: #155724; }
.import-badge-update { background: #cce5ff; color: #004085; }
.import-badge-skip { background: #e2e3e5; color: #383d41; }
.import-badge-error { background: #f8d7da; color: #721c24; }
.import-cell-error { color: #c62828; font-size: 0.75em; line-height: 1.2; margin-top: 2px; white-space: normal; }
.import-row-result-imported td { background-color: #d4edda !important; }
.import-row-result-updated  td { background-color: #cce5ff !important; }
.import-row-result-skipped  td { background-color: #e2e3e5 !important; }
.import-row-result-error    td { background-color: #f8d7da !important; }

.import-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid #ccc;
    border-top-color: #555;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.link-error-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 8px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    margin-bottom: 8px;
}
.link-error-bar .error-count {
    color: #721c24;
    font-weight: 600;
    font-size: 0.9em;
}
</style>
